import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, Shield, Key } from 'lucide-react';
import { toast } from 'sonner';

const RolesPage = () => {
  const { api, hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRoleDialog, setShowAddRoleDialog] = useState(false);
  const [showAddPermDialog, setShowAddPermDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState({ name: '', description: '', permissions: [] });
  const [newPermission, setNewPermission] = useState({ name: '', description: '', resource: '', action: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/roles/'),
        api.get('/roles/permissions')
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    try {
      await api.post('/roles/', newRole);
      toast.success('Role created successfully');
      setShowAddRoleDialog(false);
      setNewRole({ name: '', description: '', permissions: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create role');
    }
  };

  const handleUpdateRole = async () => {
    try {
      await api.put(`/roles/${editingRole.id}`, {
        name: editingRole.name,
        description: editingRole.description,
        permissions: editingRole.permissions
      });
      toast.success('Role updated successfully');
      setEditingRole(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!confirm('Are you sure you want to delete this role?')) return;
    try {
      await api.delete(`/roles/${roleId}`);
      toast.success('Role deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const handleCreatePermission = async () => {
    try {
      await api.post('/roles/permissions', newPermission);
      toast.success('Permission created');
      setShowAddPermDialog(false);
      setNewPermission({ name: '', description: '', resource: '', action: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create permission');
    }
  };

  const togglePermission = (permId, isNew = false) => {
    if (isNew) {
      setNewRole(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permId)
          ? prev.permissions.filter(p => p !== permId)
          : [...prev.permissions, permId]
      }));
    } else if (editingRole) {
      setEditingRole(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permId)
          ? prev.permissions.filter(p => p !== permId)
          : [...prev.permissions, permId]
      }));
    }
  };

  // Group permissions by resource
  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.resource]) acc[perm.resource] = [];
    acc[perm.resource].push(perm);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Roles & Permissions</h1>
            <p className="text-gray-500">Manage access control</p>
          </div>
        </div>

        <Tabs defaultValue="roles" className="w-full">
          <TabsList>
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Roles
            </TabsTrigger>
            <TabsTrigger value="permissions" className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              Permissions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="roles" className="mt-6">
            <div className="flex justify-end mb-4">
              {hasPermission('perm_roles_create') && (
                <Dialog open={showAddRoleDialog} onOpenChange={setShowAddRoleDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#e85a2c] hover:bg-[#d14a1e]">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Role
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Role</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Role Name</Label>
                        <Input
                          value={newRole.name}
                          onChange={(e) => setNewRole(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Manager"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={newRole.description}
                          onChange={(e) => setNewRole(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Role description"
                        />
                      </div>
                      <div>
                        <Label className="mb-3 block">Permissions</Label>
                        <div className="space-y-4 max-h-60 overflow-y-auto border rounded-lg p-4">
                          {Object.entries(groupedPermissions).map(([resource, perms]) => (
                            <div key={resource}>
                              <p className="font-medium text-gray-700 capitalize mb-2">{resource}</p>
                              <div className="grid grid-cols-2 gap-2 pl-4">
                                {perms.map(perm => (
                                  <label key={perm.id} className="flex items-center gap-2 text-sm">
                                    <Checkbox
                                      checked={newRole.permissions.includes(perm.id)}
                                      onCheckedChange={() => togglePermission(perm.id, true)}
                                    />
                                    {perm.name}
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button onClick={handleCreateRole} className="w-full bg-[#e85a2c] hover:bg-[#d14a1e]">
                        Create Role
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <div className="grid gap-4">
              {roles.map((role) => (
                <div key={role.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-[#e85a2c]" />
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{role.name}</h3>
                        {role.is_system && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">System</span>
                        )}
                      </div>
                      <p className="text-gray-500 mt-1">{role.description}</p>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {role.permissions?.slice(0, 5).map((permId) => {
                          const perm = permissions.find(p => p.id === permId);
                          return perm ? (
                            <span key={permId} className="px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded">
                              {perm.name}
                            </span>
                          ) : null;
                        })}
                        {role.permissions?.length > 5 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                            +{role.permissions.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {hasPermission('perm_roles_update') && (
                        <Dialog open={editingRole?.id === role.id} onOpenChange={(open) => !open && setEditingRole(null)}>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" onClick={() => setEditingRole({...role})}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Edit Role: {editingRole?.name}</DialogTitle>
                            </DialogHeader>
                            {editingRole && (
                              <div className="space-y-4">
                                <div>
                                  <Label>Role Name</Label>
                                  <Input
                                    value={editingRole.name}
                                    onChange={(e) => setEditingRole(prev => ({ ...prev, name: e.target.value }))}
                                  />
                                </div>
                                <div>
                                  <Label>Description</Label>
                                  <Input
                                    value={editingRole.description}
                                    onChange={(e) => setEditingRole(prev => ({ ...prev, description: e.target.value }))}
                                  />
                                </div>
                                <div>
                                  <Label className="mb-3 block">Permissions</Label>
                                  <div className="space-y-4 max-h-60 overflow-y-auto border rounded-lg p-4">
                                    {Object.entries(groupedPermissions).map(([resource, perms]) => (
                                      <div key={resource}>
                                        <p className="font-medium text-gray-700 capitalize mb-2">{resource}</p>
                                        <div className="grid grid-cols-2 gap-2 pl-4">
                                          {perms.map(perm => (
                                            <label key={perm.id} className="flex items-center gap-2 text-sm">
                                              <Checkbox
                                                checked={editingRole.permissions?.includes(perm.id)}
                                                onCheckedChange={() => togglePermission(perm.id)}
                                              />
                                              {perm.name}
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <Button onClick={handleUpdateRole} className="w-full bg-[#e85a2c] hover:bg-[#d14a1e]">
                                  Save Changes
                                </Button>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                      )}
                      {hasPermission('perm_roles_delete') && !role.is_system && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteRole(role.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="permissions" className="mt-6">
            <div className="flex justify-end mb-4">
              {hasPermission('perm_roles_create') && (
                <Dialog open={showAddPermDialog} onOpenChange={setShowAddPermDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#e85a2c] hover:bg-[#d14a1e]">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Permission
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Permission</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Permission Name</Label>
                        <Input
                          value={newPermission.name}
                          onChange={(e) => setNewPermission(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., View Reports"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={newPermission.description}
                          onChange={(e) => setNewPermission(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Permission description"
                        />
                      </div>
                      <div>
                        <Label>Resource</Label>
                        <Input
                          value={newPermission.resource}
                          onChange={(e) => setNewPermission(prev => ({ ...prev, resource: e.target.value }))}
                          placeholder="e.g., reports, invoices"
                        />
                      </div>
                      <div>
                        <Label>Action</Label>
                        <Input
                          value={newPermission.action}
                          onChange={(e) => setNewPermission(prev => ({ ...prev, action: e.target.value }))}
                          placeholder="e.g., create, read, update, delete"
                        />
                      </div>
                      <Button onClick={handleCreatePermission} className="w-full bg-[#e85a2c] hover:bg-[#d14a1e]">
                        Create Permission
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">Permission</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">Resource</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">Action</th>
                    <th className="text-left py-4 px-6 text-gray-600 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {permissions.map((perm) => (
                    <tr key={perm.id} className="border-b hover:bg-gray-50">
                      <td className="py-4 px-6 font-medium">
                        <div className="flex items-center gap-2">
                          <Key className="w-4 h-4 text-gray-400" />
                          {perm.name}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="px-2 py-1 bg-blue-50 text-blue-600 text-sm rounded capitalize">
                          {perm.resource}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="px-2 py-1 bg-green-50 text-green-600 text-sm rounded capitalize">
                          {perm.action}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-500">{perm.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default RolesPage;
